import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ColumnFilters,
  EditableWordFields,
  RowsSelected,
} from 'components/WordsTable';
import VocabularyVerbsView, {
  useVocabularyVerbsView,
  VerbFilterBar,
  type WordStatus,
} from 'components/vocabularyVerbsView';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { commonActions } from 'app/store/common.slice';
import { LearningSession } from 'app/types';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import { NotificationCenterContext } from 'components/NotificationCenter';
import PageIntro from 'components/PageIntro/PageIntro';
import { usePageIntro } from 'components/PageIntro/PageIntroContext';
import WithLoader from 'components/WithLoader';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import { axiosInstance } from 'setup/axiosInstance';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import TableSettingsDialog from 'components/TableSettingsDialog';
import { createDefaultColumnSettings } from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';
import CompactPagination from 'components/CompactPagination';

interface IVocabularyVerbsProps {}

type VerbViewMode = 'table' | 'cards';
type VerbGroupBy = 'letter' | 'recent' | 'none';

const DEFAULT_VOCABULARY_VERBS_COLUMNS = createDefaultColumnSettings({
  number: true,
  infinitive: true,
  infinitiveTranscription: true,
  pastSimple: true,
  pastSimpleTranscription: true,
  pastParticiple: true,
  pastParticipleTranscription: true,
  translation: true,
  isActiveSpeech: true,
  isLearned: true,
}).concat({
  key: 'description',
  order: 10,
  visible: false,
});

const VocabularyVerbs: React.FC<IVocabularyVerbsProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pushNotification } = useContext(NotificationCenterContext);
  // Same pattern as VocabularyWordsPage — when the hero is collapsed, give
  // the filter bar breathing room from the breadcrumb-row CTA above it.
  const { collapsed: introCollapsed } = usePageIntro();
  const { data, loading } = useAppSelector(
    (store) => store.common.vocabularyVerbs,
  );
  const [rowsSelected, setRowsSelected] = useState<RowsSelected>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [, setEditedVerbs] = useState<Record<number, EditableWordFields>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<VerbViewMode>('table');
  const [groupBy, setGroupBy] = useState<VerbGroupBy>('letter');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({
    isActiveSpeech: 'all',
    isLearned: 'all',
  });
  const [search, setSearch] = useState('');
  const [learningStartLoading, setLearningStartLoading] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const {
    resetSettings: resetTableSettings,
    saveSettings: saveTableSettings,
    settings: tableSettings,
  } = useTableSettings(
    'VOCABULARY_IRREGULAR_VERBS',
    DEFAULT_VOCABULARY_VERBS_COLUMNS,
  );
  const verbsPageSize = tableSettings.pageSize;
  const selectedVerbIds = useMemo(
    () =>
      Object.entries(rowsSelected)
        .filter(([, selected]) => selected)
        .map(([id]) => Number(id)),
    [rowsSelected],
  );

  const filteredVerbs = useMemo(
    () =>
      data.filter((verb) => {
        const searchValue = search.trim().toLowerCase();

        if (searchValue) {
          const haystack = [
            verb.infinitive,
            verb.infinitiveTranscription,
            verb.pastSimple,
            verb.pastSimpleTranscription,
            verb.pastParticiple,
            verb.pastParticipleTranscription,
            verb.translation,
            verb.description,
            verb.levelBased,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          if (!haystack.includes(searchValue)) {
            return false;
          }
        }

        if (columnFilters.isLearned === 'true' && !verb.isLearned) return false;
        if (columnFilters.isLearned === 'false' && verb.isLearned) return false;
        if (columnFilters.isActiveSpeech === 'true' && !verb.isActiveSpeech) return false;
        if (columnFilters.isActiveSpeech === 'false' && verb.isActiveSpeech) return false;

        return true;
      }),
    [columnFilters, data, search],
  );
  const overviewStats = useMemo(() => {
    const total = data.length;
    const learned = data.filter((verb) => verb.isLearned).length;
    const activeSpeech = data.filter((verb) => verb.isActiveSpeech).length;
    const needsAttention = data.filter(
      (verb) => !verb.isLearned && !verb.isActiveSpeech,
    ).length;
    const repeat = data.filter((verb) => !verb.isLearned).length;
    const levelCounts = data.reduce<Record<string, number>>((acc, verb) => {
      const level = verb.levelBased || '—';
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      learned,
      activeSpeech,
      needsAttention,
      repeat,
      learnedPct: total ? Math.round((learned / total) * 100) : 0,
      activePct: total ? Math.round((activeSpeech / total) * 100) : 0,
      levelCounts,
    };
  }, [data]);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredVerbs.length / verbsPageSize),
  );
  const sortedVerbs = useMemo(() => {
    if (groupBy === 'recent') {
      return [...filteredVerbs].sort((a, b) => {
        const av = a.addedAt ?? '';
        const bv = b.addedAt ?? '';
        if (av && bv) return bv.localeCompare(av);
        return b.id - a.id;
      });
    }

    if (groupBy === 'letter') {
      return [...filteredVerbs].sort((a, b) =>
        (a.infinitive ?? '').localeCompare(b.infinitive ?? ''),
      );
    }

    return filteredVerbs;
  }, [filteredVerbs, groupBy]);
  const paginatedVerbs = useMemo(
    () =>
      sortedVerbs.slice(
        (currentPage - 1) * verbsPageSize,
        currentPage * verbsPageSize,
      ),
    [currentPage, sortedVerbs, verbsPageSize],
  );

  // New rendering pipeline (vocabularyVerbsView/* module). The hook drives the
  // table + cards rendering below; the page still owns search/filters/group-by
  // controls in the toolbar above and pushes them into the hook via the sync
  // effects right after. The hook's selection is mirrored back into the legacy
  // `rowsSelected` state so the existing selection toolbar keeps working.
  const verbsView = useVocabularyVerbsView({
    verbs: paginatedVerbs,
    resetKey: currentPage,
  });

  // Mirror the hook's search back into the page's `search` state so the legacy
  // filter chain (filteredVerbs → sortedVerbs → paginatedVerbs) still narrows
  // by what the user typed into the FilterBar.
  useEffect(() => {
    setSearch(verbsView.search);
  }, [verbsView.search]);

  // Translate the hook's 4-state statusFilter into the legacy
  // {isLearned, isActiveSpeech} pair so the legacy filter chain reflects the
  // status chip the user picked in the FilterBar.
  useEffect(() => {
    const sf = verbsView.statusFilter;
    if (sf === 'ACTIVE')
      setColumnFilters({ isActiveSpeech: 'true', isLearned: 'all' });
    else if (sf === 'LEARNED')
      setColumnFilters({ isActiveSpeech: 'all', isLearned: 'true' });
    else if (sf === 'REPEAT' || sf === 'NEW')
      setColumnFilters({ isActiveSpeech: 'false', isLearned: 'false' });
    else setColumnFilters({ isActiveSpeech: 'all', isLearned: 'all' });
  }, [verbsView.statusFilter]);

  // The FilterBar now writes directly into the hook (verbsView.setGroupBy,
  // verbsView.setViewMode). The legacy page-level `groupBy` / `viewMode`
  // state still drives `sortedVerbs` and a few stale render branches, so we
  // mirror hook → page (NOT the other way round — that direction creates a
  // loop that overwrites every click the user makes in the FilterBar, which
  // was the bug behind "Картки і групування не працюють").
  useEffect(() => {
    setGroupBy(verbsView.groupBy);
     
  }, [verbsView.groupBy]);

  useEffect(() => {
    setViewMode(verbsView.viewMode);
     
  }, [verbsView.viewMode]);

  // Mirror the hook's selection back into rowsSelected so the existing
  // selection toolbar (edit / delete / start-learning buttons) reads the
  // same set of ids without having to refactor every caller.
  useEffect(() => {
    setRowsSelected(verbsView.selected);
    // We intentionally do NOT depend on rowsSelected — the hook is the
    // source of truth; the page's state is a derived mirror.
     
  }, [verbsView.selected]);

  const handleSetVerbStatus = useCallback(
    (id: number, status: WordStatus) => {
      const verb = (data ?? []).find((v) => v.id === id);
      if (!verb) return;
      // BE still stores the lifecycle as two booleans; map the 4-state enum
      // back. REPEAT lives only on the words side for now — we coerce it to
      // LEARNED on the verb path so a verb marked as REPEAT round-trips as
      // "learned but needs work" rather than "new".
      const isActiveSpeech = status === 'ACTIVE';
      const isLearned =
        status === 'ACTIVE' || status === 'LEARNED' || status === 'REPEAT';
      dispatch(
        commonActions.vocabularyVerbsUpdate({
          verbs: [{ ...verb, isActiveSpeech, isLearned }],
        }),
      );
    },
    [data, dispatch],
  );

  const handleDeleteOneVerb = useCallback(
    (id: number) => {
      dispatch(commonActions.vocabularyVerbsDelete({ verbIds: [id] }));
    },
    [dispatch],
  );

  const handleBulkSetStatus = useCallback(
    (status: WordStatus) => {
      const ids = verbsView.selectedIds;
      if (!ids.length) return;
      const isActiveSpeech = status === 'ACTIVE';
      const isLearned =
        status === 'ACTIVE' || status === 'LEARNED' || status === 'REPEAT';
      const target = new Set(ids);
      const verbs = (data ?? [])
        .filter((v) => target.has(v.id))
        .map((v) => ({ ...v, isActiveSpeech, isLearned }));
      if (!verbs.length) return;
      dispatch(commonActions.vocabularyVerbsUpdate({ verbs }));
      verbsView.clear();
    },
    [data, dispatch, verbsView],
  );

  const handleBulkDelete = useCallback(() => {
    if (!verbsView.selectedIds.length) return;
    setDeleteDialogOpen(true);
  }, [verbsView.selectedIds.length]);

  const levelOptions = useMemo(() => {
    const set = new Set<string>();
    (data ?? []).forEach((v) => {
      if (v.levelBased) set.add(v.levelBased);
    });
    // Stable CEFR order: A1..C2 first, then anything else alphabetically.
    const order = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    return [
      ...order.filter((k) => set.has(k)),
      ...Array.from(set).filter((k) => !order.includes(k)).sort(),
    ];
  }, [data]);

  const settingsAnchorRef = React.useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    dispatch(commonActions.vocabularyVerbsGet());
  }, [dispatch]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
    setRowsSelected({});
    setEditMode(false);
    setEditedVerbs({});
  }, [columnFilters, groupBy, search, verbsPageSize, viewMode]);

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    dispatch(commonActions.vocabularyVerbsDelete({ verbIds: selectedVerbIds }));
    setRowsSelected({});
    setDeleteDialogOpen(false);
  };

  const handleStartLearning = async () => {
    if (!selectedVerbIds.length) {
      return;
    }

    setLearningStartLoading(true);
    try {
      const { data: learningSession } =
        await axiosInstance.post<LearningSession>('/learning-sessions', {
          mode: 'MANUAL',
          source: 'VOCABULARY',
          irregularVerbIds: selectedVerbIds,
        });
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(learningSession.id),
        }),
      );
    } finally {
      setLearningStartLoading(false);
    }
  };

  const changePage = (page: number) => {
    if (editMode) {
      return;
    }

    setCurrentPage(page);
  };
  // Click without a selection routes to a warning toast — see the same
  // pattern on VocabularyWordsPage. Disabled state is reserved for "you
  // can't physically do this right now" cases (edit mode, async loading).
  const onStartLearningClick = () => {
    if (!selectedVerbIds.length) {
      pushNotification(
        'Спочатку оберіть дієслова, щоб почати вправи',
        'warning',
      );
      return;
    }
    handleStartLearning();
  };

  const startLearningButton = (
    <FilledActionButton
      disabled={editMode || learningStartLoading}
      onClick={onStartLearningClick}
      startIcon={<SchoolIcon sx={{ fontSize: 18 }} />}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Почати вправи
    </FilledActionButton>
  );

  const statsFooter = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr 1fr',
          md: 'repeat(3, minmax(0, 160px)) minmax(260px, 1fr)',
        },
        gap: 1.5,
      }}
    >
            {[
              { value: overviewStats.total, label: 'усього', sub: 'у словнику' },
              {
                value: overviewStats.learned,
                label: 'вивчено',
                sub: `${overviewStats.learnedPct}% словника`,
                color: 'primary.vocabularyLearned',
              },
              {
                value: overviewStats.activeSpeech,
                label: 'активне мовлення',
                sub: `${overviewStats.activePct}% словника`,
                color: 'primary.vocabularyActiveSpeech',
              },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2.25,
                  py: 1.75,
                }}
              >
                <Box
                  sx={{
                    color: stat.color || 'text.primary',
                    fontSize: { xs: 24, md: 28 },
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </Box>
                <Box sx={{ color: 'text.secondary', fontSize: 13, mt: 0.75 }}>
                  {stat.label}
                </Box>
                <Box sx={{ color: 'text.secondary', fontSize: 12.5, mt: 0.25 }}>
                  {stat.sub}
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                gridColumn: { xs: '1 / -1', md: 'auto' },
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2.25,
                py: 1.75,
              }}
            >
              <Box
                sx={{
                  color: 'text.secondary',
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  mb: 1.25,
                }}
              >
                Розподіл за рівнями
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  height: 10,
                  borderRadius: 999,
                  overflow: 'hidden',
                  bgcolor: 'primary.body',
                  mb: 1.25,
                }}
              >
                {Object.entries(overviewStats.levelCounts).map(
                  ([level, count], index) => (
                    <Box
                      key={level}
                      title={`${level}: ${count}`}
                      sx={{
                        width: `${
                          overviewStats.total
                            ? (count / overviewStats.total) * 100
                            : 0
                        }%`,
                        bgcolor: [
                          'primary.main',
                          'primary.aqua',
                          'secondary.main',
                          'primary.purpleDark',
                          'primary.vocabularyLearned',
                          'primary.vocabularyActiveSpeech',
                        ][index % 6],
                      }}
                    />
                  ),
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
                {Object.entries(overviewStats.levelCounts).map(
                  ([level, count]) => (
                    <Box
                      key={level}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: 12.5,
                      }}
                    >
                      <Box sx={{ color: 'text.primary', fontWeight: 800 }}>
                        {level}
                      </Box>
                      <Box sx={{ color: 'text.secondary' }}>{count}</Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          </Box>
  );

  return (
    <>
      <UIContainer>
        <BreadCrumbs actions={startLearningButton} />
      </UIContainer>
      <WithLoader loading={loading}>
        <UIContainer sx={{ mb: 2, pb: { xs: 4, md: 8 } }}>
          <PageIntro
            title={'Мій словник неправильних дієслів'}
            description={
              'Три форми кожного дієслова, статуси, редагування і вправи в одному робочому списку.'
            }
            footer={statsFooter}
          />

          <Box sx={{ mt: introCollapsed ? 2.5 : 0 }}>
          <VerbFilterBar
            view={verbsView}
            levelOptions={levelOptions}
            onBulkSetStatus={handleBulkSetStatus}
            onBulkDelete={handleBulkDelete}
            settingsAnchorRef={settingsAnchorRef}
            onToggleSettings={() =>
              setSettingsAnchor((prev) =>
                prev ? null : settingsAnchorRef.current,
              )
            }
            settingsOpen={Boolean(settingsAnchor)}
          />
          </Box>

          {/*
          <Box>
            <UIFlex spacing={1}>
              <Button
                disabled={
                  editMode || !selectedVerbIds.length || learningStartLoading
                }
                onClick={handleStartLearning}
                variant={'contained'}
              >
                {'Почати вправи на вивчення'}
              </Button>
            </UIFlex>
            <UIBox>
              <UIFlex spacing={1}>
                {editMode && (
                  <Button onClick={handleEditCancel}>
                    <CloseIcon />
                  </Button>
                )}
                <Button
                  disabled={
                    !selectedVerbIds.length || (editMode && !hasEditedVerbs)
                  }
                  onClick={handleEditClick}
                >
                  {editMode ? <SaveIcon /> : <EditIcon />}
                </Button>
                <Button
                  disabled={editMode || !selectedVerbIds.length}
                  onClick={handleDeleteClick}
                >
                  <TrashIcon />
                </Button>
                <Button
                  disabled={editMode}
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                >
                  <SettingsIcon />
                </Button>
              </UIFlex>
            </UIBox>
          </Box>
          */}
          <VocabularyVerbsView
            view={verbsView}
            onSetStatus={handleSetVerbStatus}
            onDeleteOne={handleDeleteOneVerb}
          />
          {totalPages > 1 && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                mt: 3,
              }}
            >
              <CompactPagination
                currentPage={currentPage}
                disabled={editMode}
                onChange={changePage}
                totalPages={totalPages}
              />
            </Box>
          )}
        </UIContainer>
      </WithLoader>
      <Dialog
        disableScrollLock
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{'Підтвердження видалення'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Видалити вибрані неправильні дієслова (${selectedVerbIds.length}) з вашого словника?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{'Скасувати'}</Button>
          <Button
            color={'error'}
            variant={'contained'}
            onClick={handleDeleteConfirm}
          >
            {'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
      <TableSettingsDialog
        anchorEl={settingsAnchor}
        settings={tableSettings}
        title={'Налаштування таблиці неправильних дієслів'}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetTableSettings}
        onSave={saveTableSettings}
      />
    </>
  );
};

export default VocabularyVerbs;
